import { AccountBalanceWallet } from '@assets/svgs/AccountBalanceWallet'
import { InvoicingIcon } from '@assets/svgs/InvoicingIcon'
import { InfoIcon } from '@assets/svgs/InfoIcon'
import { TrendingDown } from '@assets/svgs/TrendingDown'
import { TrendingUp } from '@assets/svgs/TrendingUp'
import { currencyMask } from '@utils/masks/currencyMask'
import { TooltipExplanation } from '@components/shared/TooltipExplanation/inde'
import { PreviewCard } from '../PreviewCard'
import type { PeopleBigNumbersDataProps } from '../../context/types'

interface ConsolidatedPeopleProps {
  data: PeopleBigNumbersDataProps | null
}

const formatCurrencyWithSign = (value: number) => {
  const isNegative = value < 0
  const formatted = currencyMask(Math.abs(value).toFixed(2))
  return isNegative ? `-${formatted}` : formatted
}

const formatPercentageWithSign = (value: number) => {
  return value < 0 ? `${value.toFixed(2)}%`.replace('.', ',') : `${value.toFixed(2)}%`.replace('.', ',')
}

export const consolidatedPeople = ({ data }: ConsolidatedPeopleProps) => {
  if (!data) return []

  // Valores exibidos fiel ao backend; Desvio usa deviationValue e deviationPercentage da API
  const deviationValue = data.deviationValue ?? 0
  const deviationPercentage = data.deviationPercentage ?? 0
  const isDeviationNegative = deviationValue < 0

  const getDeviationStatus = () =>
    isDeviationNegative ? 'Acima do Budget' : 'Abaixo do Budget'

  // Budget: backend pode enviar positivo; exibir como despesa (negativo) mantendo cor atual (sem vermelho)
  const formatExpenseCurrency = (value: number) => `-${currencyMask(Math.abs(value).toFixed(2))}`

  return [
    <PreviewCard
      key="budget"
      label="Budget"
      value={formatExpenseCurrency(data.budgetAmount)}
      percentage={formatPercentageWithSign(data.budgetPercentage)}
      description="Orçamento aprovado"
      icon={<AccountBalanceWallet />}
      cardType="budget"
      infoIcon={
        <TooltipExplanation
          data={[
            {
              label: 'Budget (%) = (Orçamento de Pessoal (R$) ÷ Budget de Faturamento (R$)) × 100'
            }
          ]}
        >
          <InfoIcon />
        </TooltipExplanation>
      }
    />,
    <PreviewCard
      key="preview"
      label={`Prévia ${data.currentPreviewNumber ? `${data.currentPreviewNumber}ª semana` : '3ª semana'}`}
      value={formatCurrencyWithSign(data.previewCurrentAmount)}
      percentage={formatPercentageWithSign(data.previewCurrentPercentage)}
      description="Previsão atual"
      icon={<InvoicingIcon />}
      cardType="preview"
      isNegative={data.previewCurrentAmount < 0}
      previewWeek={data.currentPreviewNumber ?? 3}
      infoIcon={
        <TooltipExplanation
          data={[
            {
              label: 'Prévia (%) = (Total Pessoal (R$) ÷ Total de Faturamento (R$)) × 100'
            },
            {
              label: 'Prévia (R$) = Total Pessoal (R$)'
            }
          ]}
        >
          <InfoIcon />
        </TooltipExplanation>
      }
    />,
    <PreviewCard
      key="deviation"
      label="Desvio"
      value={formatCurrencyWithSign(deviationValue)}
      percentage={`${deviationPercentage.toFixed(2)}%`.replace('.', ',')}
      description={getDeviationStatus()}
      icon={isDeviationNegative ? <TrendingUp /> : <TrendingDown />}
      cardType="deviation"
      isNegative={isDeviationNegative}
      infoIcon={
        <TooltipExplanation
          data={[
            { label: 'Desvio (%) = ', text: 'Prévia (%) - Budget (%)' },
            { label: 'Desvio (R$) = ', text: 'Prévia (R$) - Budget (R$)' }
          ]}
        >
          <InfoIcon />
        </TooltipExplanation>
      }
    />
  ]
}
